import { Configuration, PublicClientApplication } from '@azure/msal-browser';

const MS_SCOPES = ['User.Read'];

// Patch history.replaceState to preserve Vue Router's state keys when MSAL overwrites it
function patchHistoryReplaceState() {
  const original = history.replaceState.bind(history);
  history.replaceState = (
    state: unknown,
    title: string,
    url?: string | URL | null,
  ) => {
    const merged =
      typeof state === 'object' && state !== null
        ? { ...history.state, ...state }
        : history.state;
    original(merged, title, url);
  };

  return () => {
    history.replaceState = original;
  };
}

function createMsalApp(tenantId: string, clientId: string) {
  const msalConfig: Configuration = {
    auth: {
      clientId,
      authority: `https://login.microsoftonline.com/${tenantId}`,
      redirectUri: `${window.location.origin}/auth/callback`,
      navigateToLoginRequestUrl: false, // Don't navigate after login
    },
    cache: {
      cacheLocation: 'sessionStorage', // Use sessionStorage for better security
      storeAuthStateInCookie: false,
    },
    system: {
      allowRedirectInIframe: true, // Allow popup redirects
      loggerOptions: {
        logLevel:
          import.meta.env.DEV || window.location.hostname.startsWith('bauwens')
            ? 3
            : 1, // Verbose in dev and for bauwens, errors only in prod
        loggerCallback: (_level, message, containsPii) => {
          if (
            (!containsPii && import.meta.env.DEV) ||
            window.location.hostname.startsWith('bauwens')
          ) {
            console.log(`[MSAL] ${message}`);
          }
        },
      },
    },
  };

  return new PublicClientApplication(msalConfig);
}

async function initMsalApp(tenantId: string, clientId: string) {
  const app = createMsalApp(tenantId, clientId);
  await app.initialize();

  return app;
}

async function acquireTokenSilent(app: PublicClientApplication) {
  const accounts = app.getAllAccounts();
  if (!accounts.length) {
    throw new Error('No MSAL account found. User must log in again.');
  }
  app.setActiveAccount(accounts[0]);
  try {
    return await app.acquireTokenSilent({ scopes: MS_SCOPES });
  } catch (err: unknown) {
    const code = (err as { errorCode?: string })?.errorCode;
    if (code === 'monitor_window_timeout' || code === 'interaction_required') {
      throw new Error(
        'Microsoft session expired. Please log out and log in again to sync your photo.',
      );
    }
    throw err;
  }
}

export async function entraGetTokenSilent(
  tenantId: string,
  clientId: string,
): Promise<string> {
  const app = await initMsalApp(tenantId, clientId);

  return (await acquireTokenSilent(app)).accessToken;
}

export async function entraLogin(tenantId: string, clientId: string) {
  const app = await initMsalApp(tenantId, clientId);

  const unpatch = patchHistoryReplaceState();
  try {
    const redirectResponse = await app.handleRedirectPromise();
    if (redirectResponse) {
      return {
        idToken: redirectResponse.idToken,
        accessToken: redirectResponse.accessToken,
      };
    }

    try {
      const { idToken, accessToken } = await acquireTokenSilent(app);

      return { idToken, accessToken };
    } catch {
      await app.loginRedirect({ scopes: MS_SCOPES });
      throw new Error('Redirect initiated');
    }
  } finally {
    unpatch();
  }
}
